/*
  ml-lead-guard — every email capture goes to /apps/precision/lead, invisibly screened for bots.

  WHY (29 Sep 2026): from 25 Sep a script in a Frankfurt datacenter submitted the footer newsletter
  form ~340 times a day with real people's addresses. Shopify's hCaptcha stops that on its native
  /contact form, but it also puts an image puzzle in front of real people, hard-fails the
  data-nocaptcha forms ("Missing CAPTCHA token"), and never guarded /apps/precision/lead at all.
  So the theme's capture forms post to the lead endpoint (which tags the Shopify customer and
  subscribes them in ESP3), and that endpoint screens each request. People see nothing new.

  1. SIGNALS for the endpoint (src/lib/lead-guard.ts in monthlees-precision-backend), added to every
     POST to /apps/precision/lead by wrapping fetch, so every existing caller is covered unchanged:
       _mlg  v1.<ms since this script ran>.<nonce>.<sig>, the sig bound to the address (salt shared
             with the backend; it proves the storefront script ran for THIS email, not a secret)
       _hp   the honeypot: an off-screen input added to every email form; people never fill it
       _mli  count of real (isTrusted) key/pointer/touch events on the page
       _mlw  navigator.webdriver
  2. FORMS: a Shopify customer form marked data-ml-lead-form="<source>" (and data-nocaptcha="true",
     so Shopify's hCaptcha never binds it) is sent here instead of natively. Optional attributes:
       data-ml-lead-goal, data-ml-lead-redirect (go there on success),
       data-ml-lead-custom (the section's own JS renders the result from the ml:lead event).
     A <template data-ml-lead-ok> inside the form is the success state (the section's own markup);
     <template data-ml-lead-err> with a [data-ml-lead-err-text] slot is the error.
     Every result is dispatched as `ml:lead` {ok, email, source, message} on the form.
*/
(function () {
  'use strict';
  if (window.MlLeadGuard) return;

  var LEAD_RE = /\/apps\/precision\/lead(?:[?#]|$)/;
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  var SALT = 'mlg1-2026-09';
  var HP_NAME = 'mlg_note';
  var clock = function () { return window.performance && performance.now ? performance.now() : Date.now(); };
  var t0 = clock();
  var nonce = (Math.random().toString(36) + '00000000').slice(2, 10);
  var touches = 0;
  var lastForm = null;

  ['keydown', 'pointerdown', 'touchstart'].forEach(function (type) {
    document.addEventListener(type, function (e) { if (e.isTrusted) touches++; }, { capture: true, passive: true });
  });

  // FNV-1a 32-bit, base36 — identical to fnv1a() in the backend's lead-guard.ts.
  function fnv(s) {
    var h = 0x811c9dc5;
    for (var i = 0; i < s.length; i++) {
      h ^= s.charCodeAt(i);
      h = Math.imul(h, 0x01000193) >>> 0;
    }
    return h.toString(36);
  }

  function token(email) {
    var ms = Math.max(0, Math.round(clock() - t0));
    var em = String(email || '').trim().toLowerCase();
    return 'v1.' + ms + '.' + nonce + '.' + fnv('v1.' + ms + '.' + nonce + '.' + em + '.' + SALT);
  }

  function emailInput(form) {
    return form && form.querySelector ? form.querySelector('input[type="email"], input[name="contact[email]"], input[name="email"]') : null;
  }

  // Only forms whose email goes to the lead endpoint: ours (data-ml-lead-form) and the script-driven
  // captures that have no native action. Native Shopify forms (login, contact) are left untouched.
  function isLeadForm(form) {
    if (form.hasAttribute('data-ml-lead-form') || form.getAttribute('data-nocaptcha') === 'true') return true;
    var a = form.getAttribute('action');
    return !a || a === '#' || a.indexOf('/apps/precision/') === 0;
  }

  function ensureHoneypot(form) {
    if (!form || !form.querySelector || form.querySelector('[data-mlg-hp]') || !emailInput(form) || !isLeadForm(form)) return;
    var i = document.createElement('input');
    i.type = 'text';
    i.name = HP_NAME;
    i.value = '';
    i.tabIndex = -1;
    i.setAttribute('autocomplete', 'off');
    i.setAttribute('aria-hidden', 'true');
    i.setAttribute('data-mlg-hp', '');
    i.style.cssText = 'position:absolute!important;left:-10000px!important;top:auto!important;width:1px!important;height:1px!important;opacity:0!important;pointer-events:none!important;';
    form.appendChild(i);
  }

  function honeypotFor(email) {
    var em = String(email || '').trim().toLowerCase();
    var forms = document.querySelectorAll('form');
    for (var i = 0; i < forms.length; i++) {
      var inp = emailInput(forms[i]);
      if (inp && String(inp.value || '').trim().toLowerCase() === em) {
        var hp = forms[i].querySelector('[data-mlg-hp]');
        return hp ? String(hp.value || '') : '';
      }
    }
    var h = lastForm && lastForm.querySelector('[data-mlg-hp]');
    return h ? String(h.value || '') : '';
  }

  function fields(email) {
    return { _mlg: token(email), _hp: honeypotFor(email), _mli: touches, _mlw: navigator.webdriver ? '1' : '0' };
  }

  // 1. Signals on every lead POST, whichever script sends it.
  var nativeFetch = window.fetch;
  if (typeof nativeFetch === 'function') {
    window.fetch = function (input, init) {
      try {
        var url = typeof input === 'string' ? input : (input && input.url) || '';
        if (LEAD_RE.test(url) && init && typeof init.body === 'string' && String(init.method || 'GET').toUpperCase() === 'POST') {
          var body = JSON.parse(init.body);
          if (body && typeof body === 'object' && body.email && !body._mlg) {
            var f = fields(body.email);
            for (var k in f) if (!(k in body)) body[k] = f[k];
            init = Object.assign({}, init, { body: JSON.stringify(body) });
          }
        }
      } catch (e) { /* never block a capture over the screen */ }
      return nativeFetch.call(window, input, init);
    };
  }

  function armForms(root) {
    var forms = (root || document).querySelectorAll ? (root || document).querySelectorAll('form') : [];
    for (var i = 0; i < forms.length; i++) ensureHoneypot(forms[i]);
  }
  document.addEventListener('focusin', function (e) {
    var f = e.target && e.target.form;
    if (f) { ensureHoneypot(f); lastForm = f; }
  }, true);

  // 2. Shopify customer forms sent to the lead endpoint.
  function emit(form, detail) {
    try { form.dispatchEvent(new CustomEvent('ml:lead', { bubbles: true, detail: detail })); } catch (e) {}
  }

  function setBusy(form, busy) {
    var btns = form.querySelectorAll('button[type="submit"], input[type="submit"], button:not([type])');
    for (var i = 0; i < btns.length; i++) {
      if (busy) { btns[i].setAttribute('aria-busy', 'true'); btns[i].disabled = true; }
      else { btns[i].removeAttribute('aria-busy'); btns[i].disabled = false; }
    }
  }

  function clearMessages(form) {
    var old = form.querySelectorAll('[data-ml-lead-msg]');
    for (var i = 0; i < old.length; i++) old[i].parentNode.removeChild(old[i]);
  }

  function showFromTemplate(form, sel, text) {
    var tpl = form.querySelector('template' + sel);
    var node;
    if (tpl && tpl.content) {
      node = document.createElement('div');
      node.appendChild(tpl.content.cloneNode(true));
      node = node.firstElementChild || node;
    } else {
      node = document.createElement('p');
      node.setAttribute('role', sel === '[data-ml-lead-err]' ? 'alert' : 'status');
      node.tabIndex = -1;
      node.textContent = text || '';
    }
    if (text) {
      var slot = node.matches && node.matches('[data-ml-lead-err-text]') ? node : node.querySelector && node.querySelector('[data-ml-lead-err-text]');
      if (slot) slot.textContent = text;
    }
    node.setAttribute('data-ml-lead-msg', '');
    return node;
  }

  function success(form, email, source) {
    try { localStorage.setItem('pss_email', email); localStorage.setItem('ml_lead_captured', '1'); } catch (e) {}
    try { sessionStorage.setItem('pss_email_captured', '1'); } catch (e) {}
    emit(form, { ok: true, email: email, source: source });
    var go = form.getAttribute('data-ml-lead-redirect');
    if (form.hasAttribute('data-ml-lead-custom')) {
      if (go) window.location.assign(go);
      return;
    }
    clearMessages(form);
    var kids = form.children;
    for (var i = 0; i < kids.length; i++) {
      var k = kids[i];
      if (k.tagName === 'TEMPLATE' || (k.tagName === 'INPUT' && (k.type === 'hidden' || k.hasAttribute('data-mlg-hp')))) continue;
      k.hidden = true;
      k.setAttribute('data-ml-lead-hid', '');
    }
    var msg = showFromTemplate(form, '[data-ml-lead-ok]', form.getAttribute('data-ml-lead-ok-text') || 'Thanks, you’re subscribed.');
    form.appendChild(msg);
    var focusEl = msg.hasAttribute('tabindex') ? msg : msg.querySelector('[tabindex], a, button');
    if (focusEl && focusEl.focus) { try { focusEl.focus({ preventScroll: true }); } catch (e) { focusEl.focus(); } }
    if (go) window.setTimeout(function () { window.location.assign(go); }, 700);
  }

  function failure(form, input, message, source) {
    emit(form, { ok: false, message: message, source: source });
    if (form.hasAttribute('data-ml-lead-custom')) return;
    clearMessages(form);
    var msg = showFromTemplate(form, '[data-ml-lead-err]', message);
    form.insertBefore(msg, form.firstChild);
    if (input) { input.setAttribute('aria-invalid', 'true'); input.focus(); }
  }

  function send(form, e) {
    e.preventDefault();
    if (form.__mlLeadBusy) return;
    var source = form.getAttribute('data-ml-lead-form') || 'newsletter';
    var input = emailInput(form);
    var email = String((input && input.value) || '').trim();
    if (!EMAIL_RE.test(email)) {
      failure(form, input, form.getAttribute('data-ml-lead-invalid') || 'Enter a valid email address.', source);
      return;
    }
    if (input) input.removeAttribute('aria-invalid');
    form.__mlLeadBusy = true;
    setBusy(form, true);
    var body = { email: email, source: source };
    var goal = form.getAttribute('data-ml-lead-goal');
    if (goal) body.goal = goal;
    window.fetch('/apps/precision/lead', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    })
      .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); success(form, email, source); })
      .catch(function () { failure(form, input, 'That didn’t go through. Please try again in a moment.', source); })
      .then(function () { form.__mlLeadBusy = false; setBusy(form, false); });
  }

  // Capture phase on document: runs before any section's own submit handler, which still sees
  // the event (for busy states and analytics) but can't let the native POST happen.
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (form && form.matches && form.matches('form[data-ml-lead-form]')) send(form, e);
  }, true);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { armForms(); });
  else armForms();

  window.MlLeadGuard = { fields: fields, arm: armForms };
})();
